import { useState } from "react";
import { BlockTitle } from "@blocks/Blocks";
import Ad from "@components/Advertisement/Ad";
import AdSettings from "@components/Advertisement/AdSettings";
import Container from "@components/Container/Container";
import GAData from "@components/GAData/GAData";
import Column from "@components/Grid/Column";
import IconFactory from "@components/Icon/IconFactory";
import { IconTypeEnum } from "@components/Icon/utils/types";
import LoadMore from "@components/LoadMore/LoadMore";
import MetaTags from "@components/MetaTags/MetaTags";
import EventsAwardsLogoMenu from "@events-awards/components/EventsAwardsLogoMenu";
import EventsAwardsHeader from "@events-awards/EventAwardsHeader";
import { AWARDS_EVENTS_PAGE_ROW_DATA } from "@events-awards/utils/constants";
import { processArticleElements } from "@events-awards/utils/helpers";
import {
  AwardDataRow,
  EventsAwardsWinnersContextData,
} from "@events-awards/utils/types";
import { useLoadMoreEventsWinners } from "@hooks/useLoadMoreEventsWinners";
import { cn } from "@util/helpers";

import SIAFooter from "./SIAFooter";

/**
 * Component to render SIA winners page content
 */
const EventsAwardsSIAWinnerPage = ({
  title,
  articles,
  awardType,
}: EventsAwardsWinnersContextData): React.ReactElement => {
  const { articleList, handleLoadMore, hasMoreArticles } =
    useLoadMoreEventsWinners(articles, 10, awardType);
  const [showTabs, setShowTabs] = useState(false);

  const years = articleList.map(({ title }) => title);
  const [selectedYear, setSelectedYear] = useState(years[0]);

  const awardData: AwardDataRow | undefined = AWARDS_EVENTS_PAGE_ROW_DATA.find(
    (data) => data.awardType === awardType
  );
  const awardPath = awardData?.blockTitleProps?.link || "";

  return (
    <div
      className="min-h-screen w-full"
      data-testid="events-awards-sia-winners-page"
    >
      <AdSettings
        adNames={["lb1", "prestitial", "catfish", "abm"]}
        path={`${awardPath}/winners`}
        adTargets={[{ key: "page", value: "listing" }]}
      />

      <GAData
        title={`${awardData?.blockTitleProps?.text} Winners`}
        level2="events awards"
      />

      <MetaTags
        title={title}
        description={`The Business Times ${awardData?.blockTitleProps?.text} - ${awardData?.description}`}
        ogType="website"
        slug={awardPath}
      />

      <EventsAwardsHeader />

      <Container>
        <Column rootClassName="w-full text-center my-8">
          <EventsAwardsLogoMenu awardType={awardType} />

          <div className="-mx-0 w-full bg-gray-150 px-6 py-4 font-public-sans">
            <div>
              <button
                onClick={() => {
                  setShowTabs(!showTabs);
                }}
                className="mb-2 flex items-center gap-2 text-left text-2xl font-bold focus:outline-none"
              >
                <BlockTitle
                  text={selectedYear}
                  rootClassName="text-4xl md:text-6xl font-medium lg:text-8xl hover:underline"
                />

                <IconFactory
                  type={IconTypeEnum.arrowDown}
                  x={30}
                  y={20}
                  size={60}
                />
              </button>
            </div>

            {showTabs ? (
              <div className="mb-2 flex flex-wrap gap-4 border border-gray-200 bg-white p-2 shadow-sm">
                {years.map((year) => (
                  <button
                    key={year}
                    onClick={() => {
                      setSelectedYear(year);
                      setShowTabs(false);
                    }}
                    className={`px-4 py-2 text-base font-semibold transition-colors ${
                      selectedYear === year
                        ? "bg-gray-650 text-white"
                        : "bg-white text-gray-700 hover:bg-gray-100"
                    }`}
                  >
                    {year}
                  </button>
                ))}
              </div>
            ) : null}

            <div className="border-t border-gray-100 py-6">
              <div className="space-y-8">
                {articleList.find((article) => article.title === selectedYear)
                  ?.elements &&
                  processArticleElements(
                    articleList.find(
                      (article) => article.title === selectedYear
                    )?.elements || []
                  ).map((award, index) => (
                    <div key={index}>
                      <div>
                        <p
                          className={cn(
                            "tracking-wider text-left text-base font-semibold",
                            { "first:underline": !award.recipient }
                          )}
                        >
                          {award.title}
                        </p>
                      </div>

                      <div className="space-y-1 text-left">
                        {award.recipient ? (
                          award.type === "paragraph" ? (
                            <p className="text-base">{award.recipient}</p>
                          ) : (
                            <div data-testid="table-component">
                              <table className="w-full table-auto">
                                <tbody className="dark:bg-slate-800 bg-gray-50">
                                  {(typeof award.recipient === "string"
                                    ? JSON.parse(award.recipient)
                                    : award.recipient
                                  ).map((row: string[], rowIndex: number) => (
                                    <tr key={rowIndex}>
                                      {row.map((cell, cellIndex) => (
                                        <td
                                          key={cellIndex}
                                          className="border-slate-100 dark:border-slate-700 text-slate-500 dark:text-slate-400 border-b p-4"
                                        >
                                          {cell}
                                        </td>
                                      ))}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )
                        ) : null}
                      </div>

                      <div className="space-y-1 text-left text-base">
                        {award.position ? (
                          <p className="mb-0">{award.position}</p>
                        ) : null}

                        {award.organization ? (
                          <p>{award.organization}</p>
                        ) : null}
                      </div>
                    </div>
                  ))}
              </div>
            </div>

            {hasMoreArticles ? (
              <LoadMore
                onLoadMore={handleLoadMore}
                hasMore={hasMoreArticles}
                loadText="MORE"
              />
            ) : null}
          </div>
        </Column>
      </Container>

      <SIAFooter />

      <Ad adType="catfish" />

      <Ad adType="abm" />

      <Ad adType="prestitial" />
    </div>
  );
};

export default EventsAwardsSIAWinnerPage;
